import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AffiliateSetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Kein gültiger Token gefunden. Bitte verwende den Link aus deiner E-Mail.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== passwordConfirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    if (password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError('Das Passwort muss mindestens einen Großbuchstaben enthalten.');
      return;
    }
    if (!/[0-9]/.test(password)) {
      setError('Das Passwort muss mindestens eine Zahl enthalten.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/affiliate/set-password-with-token`, {
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✓</div>
          <h2 style={styles.title}>Passwort gesetzt!</h2>
          <p style={styles.text}>
            Dein Passwort wurde erfolgreich erstellt. Du kannst dich jetzt einloggen und dein
            Affiliate-Dashboard nutzen.
          </p>
          <button
            style={styles.button}
            onClick={() => navigate('/affiliate/login')}
          >
            Zum Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>AllFlowX</div>
        <h2 style={styles.title}>Passwort festlegen</h2>
        <p style={styles.subtitle}>Erstelle dein persönliches Passwort für das Partnerprogramm.</p>

        {error && (
          <div style={styles.errorBox} data-testid="set-password-error">
            {error}
          </div>
        )}

        {!token ? null : (
          <form onSubmit={handleSubmit}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Neues Passwort</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.input}
                placeholder="Mindestens 8 Zeichen, 1 Großbuchstabe, 1 Zahl"
                required
                data-testid="new-password-input"
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Passwort bestätigen</label>
              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                style={styles.input}
                placeholder="Passwort wiederholen"
                required
                data-testid="confirm-password-input"
              />
            </div>

            <div style={styles.requirements}>
              <p style={styles.reqTitle}>Passwort-Anforderungen:</p>
              <ul style={styles.reqList}>
                <li style={{ color: password.length >= 8 ? '#059669' : '#9ca3af' }}>
                  Mindestens 8 Zeichen
                </li>
                <li style={{ color: /[A-Z]/.test(password) ? '#059669' : '#9ca3af' }}>
                  Mindestens 1 Großbuchstabe
                </li>
                <li style={{ color: /[0-9]/.test(password) ? '#059669' : '#9ca3af' }}>
                  Mindestens 1 Zahl
                </li>
              </ul>
            </div>

            <button
              type="submit"
              style={{ ...styles.button, opacity: loading ? 0.7 : 1 }}
              disabled={loading}
              data-testid="set-password-submit"
            >
              {loading ? 'Wird gespeichert...' : 'Passwort speichern'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
  },
  card: {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '40px',
    width: '100%',
    maxWidth: '440px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  logo: {
    fontSize: '22px',
    fontWeight: '800',
    color: '#e63946',
    marginBottom: '20px',
    textAlign: 'center',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    margin: '0 0 8px',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
    textAlign: 'center',
    marginBottom: '24px',
  },
  errorBox: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    padding: '12px 14px',
    color: '#dc2626',
    fontSize: '14px',
    marginBottom: '16px',
  },
  fieldGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#111827',
    boxSizing: 'border-box',
    outline: 'none',
  },
  requirements: {
    background: '#f9fafb',
    borderRadius: '6px',
    padding: '12px 14px',
    marginBottom: '20px',
  },
  reqTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#374151',
    margin: '0 0 6px',
  },
  reqList: {
    margin: 0,
    paddingLeft: '16px',
    fontSize: '12px',
  },
  button: {
    width: '100%',
    padding: '12px',
    background: '#e63946',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  successIcon: {
    width: '56px',
    height: '56px',
    background: '#ecfdf5',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    color: '#059669',
    margin: '0 auto 20px',
    fontWeight: '700',
  },
  text: {
    fontSize: '14px',
    color: '#6b7280',
    textAlign: 'center',
    marginBottom: '24px',
    lineHeight: '1.6',
  },
};
